import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Save, X, ShieldCheck, AlertTriangle } from 'lucide-react';
import { Button } from '@components/Button';
import { Input } from '@components/Input';
import { Card, CardContent } from '@components/Card';
import { SimpleTable as Table, TableHeader, TableRow, TableCell } from '@components/Table';
import { PermissionGuard } from '@components/PermissionGuard';
import { acquirerService } from '@services/acquirerService';
import companyService from '@services/companyService';
import { Acquirer, AcquirerPIXConfiguration, AcquirerPIXConfigurationFormData } from '@/types/acquirer';
import { Company } from '@/types/company';
import { formatDateBR } from '@pages/financial/financialUtils';
import { certificateBadgeClass, certificateStatus } from '@/utils/certificateStatus';
import { PIXCertificateSectionGuarded } from './PIXCertificateSection';

/**
 * Situação do certificado na listagem.
 *
 * Vale a coluna porque o vencimento é a única coisa dessa tela que quebra o PIX
 * sozinha, sem ninguém mexer em nada — e antes disso não havia onde vê-la.
 */
const CertificateCell: React.FC<{ item: AcquirerPIXConfiguration }> = ({ item }) => {
  const status = certificateStatus(item.certificate_expires_at);

  if (status === 'missing') {
    // Com certificado gravado mas sem validade registrada a integração está
    // funcionando — só falta o ERP conhecer a data, o que o primeiro envio
    // pela tela resolve. Dizer "sem certificado" aqui seria alarme falso.
    const semCertificado = !item.has_certificate;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium ${certificateBadgeClass('missing')}`}>
        <AlertTriangle size={12} /> {semCertificado ? 'Sem certificado' : 'Validade não registrada'}
      </span>
    );
  }

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium ${certificateBadgeClass(status)}`}>
      <ShieldCheck size={12} />
      {status === 'expired' ? 'Vencido em ' : 'Até '}
      {formatDateBR(item.certificate_expires_at)}
    </span>
  );
};

const emptyForm: AcquirerPIXConfigurationFormData = {
  acquirer_id: 0,
  company_id: 0,
  ec: 0,
  client_id: '',
  client_secret: '',
  api_token_auth_url: '',
  api_request_url: '',
  pix_key: '',
};

export const AcquirerPIXConfigTab: React.FC = () => {
  const [items, setItems] = useState<AcquirerPIXConfiguration[]>([]);
  const [acquirers, setAcquirers] = useState<Acquirer[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [editingItem, setEditingItem] = useState<AcquirerPIXConfiguration | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<AcquirerPIXConfigurationFormData>({ ...emptyForm });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const pageSize = 20;

  const loadLookups = async () => {
    try {
      const [acqs, comps] = await Promise.all([
        acquirerService.listAllAcquirers(),
        companyService.getCompanies(),
      ]);
      setAcquirers(acqs);
      setCompanies(comps);
    } catch { /* silently ignore */ }
  };

  const loadItems = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await acquirerService.getPIXConfigs({ page: currentPage, pageSize });
      setItems(result.data);
      setTotalPages(result.pagination.totalPages);
      setTotalItems(result.pagination.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadLookups(); }, []);
  useEffect(() => { loadItems(); }, [currentPage]);

  const getAcquirerName = (id: number) => acquirers.find(a => a.id === id)?.name ?? `#${id}`;
  const getCompanyLabel = (id: number) => {
    const c = companies.find(c => c.company_id === id);
    return c ? `${c.name} — ${c.document}` : `#${id}`;
  };

  const openCreateForm = () => {
    setEditingItem(null);
    setFormData({ ...emptyForm });
    setFormErrors({});
    setShowForm(true);
  };

  const openEditForm = (item: AcquirerPIXConfiguration) => {
    setEditingItem(item);
    setFormData({
      acquirer_id: item.acquirer_id,
      company_id: item.company_id,
      ec: item.ec,
      client_id: item.client_id ?? '',
      client_secret: item.client_secret ?? '',
      api_token_auth_url: item.api_token_auth_url ?? '',
      api_request_url: item.api_request_url ?? '',
      pix_key: item.pix_key ?? '',
    });
    setFormErrors({});
    setShowForm(true);
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    if (!formData.acquirer_id) errors.acquirer_id = 'Adquirente é obrigatório';
    if (!formData.company_id) errors.company_id = 'Empresa é obrigatória';
    if (!formData.ec) errors.ec = 'EC é obrigatório';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    try {
      setSaving(true);
      setError(null);
      if (editingItem) {
        await acquirerService.updatePIXConfig(editingItem.acquirer_pix_configuration_id, formData);
      } else {
        await acquirerService.createPIXConfig(formData);
      }
      setShowForm(false);
      loadItems();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await acquirerService.deletePIXConfig(id);
      setDeleteConfirm(null);
      loadItems();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao excluir');
    }
  };

  const updateField = (field: keyof AcquirerPIXConfigurationFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) setFormErrors(prev => { const n = { ...prev }; delete n[field]; return n; });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-600">Configurações PIX por adquirente e empresa.</p>
        <PermissionGuard permission="receiving_hub.write">
          <Button size="sm" onClick={openCreateForm} icon={<Plus size={16} />}>Nova Configuração</Button>
        </PermissionGuard>
      </div>

      {error && (
        <Card className="bg-red-50 border-red-200">
          <CardContent><p className="text-red-600 text-sm">{error}</p></CardContent>
        </Card>
      )}

      {showForm && (
        <Card className="border-emerald-200 bg-emerald-50/30">
          <CardContent>
            <h3 className="text-sm font-semibold text-slate-700 mb-3">
              {editingItem ? 'Editar Configuração PIX' : 'Nova Configuração PIX'}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Adquirente <span className="text-red-500">*</span></label>
                <select className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" value={formData.acquirer_id} onChange={e => updateField('acquirer_id', Number(e.target.value))}>
                  <option value={0}>Selecione...</option>
                  {acquirers.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
                {formErrors.acquirer_id && <p className="text-xs text-red-500 mt-1">{formErrors.acquirer_id}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Empresa <span className="text-red-500">*</span></label>
                <select className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" value={formData.company_id} onChange={e => updateField('company_id', Number(e.target.value))}>
                  <option value={0}>Selecione...</option>
                  {companies.map(c => <option key={c.company_id} value={c.company_id}>{c.name} — {c.document}</option>)}
                </select>
                {formErrors.company_id && <p className="text-xs text-red-500 mt-1">{formErrors.company_id}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">EC <span className="text-red-500">*</span></label>
                <Input type="number" value={formData.ec || ''} onChange={e => updateField('ec', Number(e.target.value))} placeholder="Código EC" />
                {formErrors.ec && <p className="text-xs text-red-500 mt-1">{formErrors.ec}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Client ID</label>
                <Input value={formData.client_id} onChange={e => updateField('client_id', e.target.value)} placeholder="Client ID" maxLength={50} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Client Secret</label>
                <Input value={formData.client_secret} onChange={e => updateField('client_secret', e.target.value)} placeholder="Client Secret" maxLength={50} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">API Token Auth URL</label>
                <Input value={formData.api_token_auth_url} onChange={e => updateField('api_token_auth_url', e.target.value)} placeholder="URL de autenticação" maxLength={100} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">API Request URL</label>
                <Input value={formData.api_request_url} onChange={e => updateField('api_request_url', e.target.value)} placeholder="URL de requisição" maxLength={100} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Chave PIX</label>
                <Input value={formData.pix_key} onChange={e => updateField('pix_key', e.target.value)} placeholder="Chave PIX" maxLength={40} />
              </div>
            </div>

            {/* Caminho e senha do certificado saíram do formulário: são
                preenchidos pelo envio/renovação abaixo. Editá-los à mão
                apontaria a configuração para um arquivo que pode não existir,
                ou dessincronizaria a senha do .pfx gravado. */}
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" size="sm" onClick={() => setShowForm(false)} icon={<X size={14} />}>Cancelar</Button>
              <Button size="sm" onClick={handleSave} loading={saving} icon={<Save size={14} />}>{editingItem ? 'Salvar' : 'Criar'}</Button>
            </div>

            {/* O certificado só faz sentido depois de a configuração existir —
                o blob é nomeado pelo id dela. */}
            {editingItem && (
              <PIXCertificateSectionGuarded
                // A configuração vem da lista recarregada, não do estado
                // capturado ao abrir o formulário: é o que faz o badge de
                // validade refletir o certificado recém-enviado.
                config={
                  items.find(
                    (i) => i.acquirer_pix_configuration_id === editingItem.acquirer_pix_configuration_id,
                  ) ?? editingItem
                }
                onChanged={loadItems}
              />
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <tr>
                <th className="text-left">ID</th>
                <th className="text-left">Adquirente</th>
                <th className="text-left">Empresa</th>
                <th className="text-left">EC</th>
                <th className="text-left">Chave PIX</th>
                <th className="text-left">Certificado</th>
                <th className="text-right">Ações</th>
              </tr>
            </TableHeader>
            <tbody>
              {loading ? (
                [...Array(3)].map((_, i) => (
                  <TableRow key={i}>
                    {[...Array(7)].map((_, j) => (
                      <TableCell key={j}><div className="h-4 bg-slate-200 rounded w-16 animate-pulse" /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-slate-500 py-8">Nenhuma configuração encontrada</TableCell>
                </TableRow>
              ) : (
                items.map((item) => (
                  <TableRow key={item.acquirer_pix_configuration_id}>
                    <TableCell className="font-mono text-sm">{item.acquirer_pix_configuration_id}</TableCell>
                    <TableCell>{getAcquirerName(item.acquirer_id)}</TableCell>
                    <TableCell className="text-sm">{getCompanyLabel(item.company_id)}</TableCell>
                    <TableCell className="font-mono text-sm">{item.ec}</TableCell>
                    <TableCell className="text-sm text-slate-600">{item.pix_key ?? '—'}</TableCell>
                    <TableCell><CertificateCell item={item} /></TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <PermissionGuard permission="receiving_hub.write">
                          <Button variant="secondary" size="sm" onClick={() => openEditForm(item)} icon={<Edit size={14} />}>Editar</Button>
                        </PermissionGuard>
                        <PermissionGuard permission="receiving_hub.write">
                          {deleteConfirm === item.acquirer_pix_configuration_id ? (
                            <div className="flex items-center gap-1">
                              <Button variant="danger" size="sm" onClick={() => handleDelete(item.acquirer_pix_configuration_id)}>Confirmar</Button>
                              <Button variant="secondary" size="sm" onClick={() => setDeleteConfirm(null)}>Cancelar</Button>
                            </div>
                          ) : (
                            <Button variant="outline" size="sm" onClick={() => setDeleteConfirm(item.acquirer_pix_configuration_id)} icon={<Trash2 size={14} />}>Excluir</Button>
                          )}
                        </PermissionGuard>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </tbody>
          </Table>
        </CardContent>
      </Card>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-600">Total: {totalItems} registro(s)</p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>Anterior</Button>
            <span className="text-sm text-slate-600">Página {currentPage} de {totalPages}</span>
            <Button variant="outline" size="sm" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>Próxima</Button>
          </div>
        </div>
      )}
    </div>
  );
};
